import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { API, graphqlOperation } from 'aws-amplify';
import { useAlert } from 'react-alert';
import { getCreditPlans } from 'graphql/credits/queries';
import { createStripeCheckoutSession, createStripePortalSession } from 'graphql/credits/mutations';
import { useCredits } from 'hooks/useCredits';
import Button from 'shared/components/Button';
import SkeletonLoader from 'shared/components/ui/SkeletonLoader';
import { StarIcon, RocketIcon, DiamondIcon, CrownIcon, CheckmarkIcon, ArrowLeftIcon } from 'shared/components/Icons';
import './Subscription.scss';

const Subscription = () => {
  const history = useHistory();
  const location = useLocation();
  const alert = useAlert();
  const { subscriptionTier, subscriptionStatus, loading: creditsLoading } = useCredits();

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  useEffect(() => {
    fetchPlansAndPackages();

    // Check for success parameter from Stripe redirect
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('success') === 'true') {
      alert.success('Subscription updated successfully! Your new plan is now active.');
      // Remove the success parameter from URL
      history.replace('/profile/subscription');
    }
  }, [location.search, alert, history]);

  const fetchPlansAndPackages = async () => {
    try {
      setLoading(true);
      const plansResult = await API.graphql(graphqlOperation(getCreditPlans));
      setPlans(plansResult.data?.getCreditPlans || []);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      alert.error('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId, billingPeriod = 'monthly') => {
    setActionLoading(true);
    try {
      const { data } = await API.graphql(
        graphqlOperation(createStripeCheckoutSession, {
          input: {
            planId,
            billingPeriod: billingPeriod.toUpperCase(),
            successUrl: `${window.location.origin}/profile/subscription?success=true`,
            cancelUrl: `${window.location.origin}/profile/subscription`,
          },
        }),
      );

      if (data.createStripeCheckoutSession.error) {
        alert.error(data.createStripeCheckoutSession.error);
        return;
      }

      // Redirect to Stripe Checkout
      window.location.href = data.createStripeCheckoutSession.sessionUrl;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      alert.error('Failed to start checkout process');
    } finally {
      setActionLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setActionLoading(true);
    try {
      const { data } = await API.graphql(
        graphqlOperation(createStripePortalSession, {
          input: {
            returnUrl: window.location.href,
          },
        }),
      );

      if (data.createStripePortalSession.error) {
        alert.error(data.createStripePortalSession.error);
        return;
      }

      // Redirect to Stripe Customer Portal
      window.location.href = data.createStripePortalSession.sessionUrl;
    } catch (error) {
      console.error('Failed to create portal session:', error);
      alert.error('Failed to open billing portal');
    } finally {
      setActionLoading(false);
    }
  };

  const getTierIcon = tier => {
    switch (tier) {
      case 'FREE':
        return <StarIcon size={20} />;
      case 'STARTER':
        return <RocketIcon size={20} />;
      case 'PRO':
        return <DiamondIcon size={20} />;
      case 'PREMIUM':
        return <CrownIcon size={20} />;
      default:
        return <StarIcon size={20} />;
    }
  };

  const getTierColor = tier => {
    switch (tier) {
      case 'FREE':
        return '#64748b';
      case 'STARTER':
        return '#10b981';
      case 'PRO':
        return '#3b82f6';
      case 'PREMIUM':
        return '#f59e0b';
      default:
        return '#64748b';
    }
  };

  const isCurrentTier = tier => tier === subscriptionTier;
  const isDowngrade = tier => {
    const hierarchy = { FREE: 0, STARTER: 1, PRO: 2, PREMIUM: 3 };
    return hierarchy[tier] < hierarchy[subscriptionTier];
  };

  const getPrice = (plan, period) => {
    // Free plan is always $0
    if (plan.tier === 'FREE') {
      return 0;
    }
    return period === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
  };

  const getYearlySavings = plan => {
    // No savings calculation for Free plan
    if (plan.tier === 'FREE') {
      return { amount: 0, percentage: 0 };
    }
    const monthlyTotal = plan.monthlyPrice * 12;
    const yearlySavings = monthlyTotal - plan.yearlyPrice;
    const percentage = Math.round((yearlySavings / monthlyTotal) * 100);
    return { amount: yearlySavings, percentage };
  };

  const goBack = () => {
    history.push('/profile/credits');
  };

  const getPlanFeatures = plan => {
    // All plans have these core features
    const coreFeatures = ['AI Enhancement', 'Hourly Price Tracking', 'Virtual Try-On', 'Look Finder'];

    return coreFeatures;
  };

  if (loading || creditsLoading) {
    return (
      <div className='subscription-page'>
        <div className='subscription-header'>
          <SkeletonLoader type='basic' height='32px' width='300px' style={{ marginBottom: '8px' }} />
          <SkeletonLoader type='basic' height='16px' width='500px' />
        </div>
        <div className='subscription-content'>
          <div className='plans-grid'>
            {[1, 2, 3].map(i => (
              <SkeletonLoader key={i} type='basic' height='400px' width='100%' />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='subscription-page'>
      <div className='subscription-header'>
        <button className='back-button' onClick={goBack}>
          <ArrowLeftIcon size={20} />
          <span>Back to Credits</span>
        </button>

        <div className='header-content'>
          <h1 className='subscription-title'>Choose Your Plan</h1>
          <p className='subscription-subtitle'>Select the perfect plan for your needs. Upgrade or downgrade anytime.</p>
        </div>

        {subscriptionTier !== 'FREE' && (
          <div className='current-subscription'>
            <div className='current-plan-info'>
              <span className='current-label'>Current Plan:</span>
              <span className={`current-tier ${subscriptionTier.toLowerCase()}`}>
                {getTierIcon(subscriptionTier)}
                {subscriptionTier}
              </span>
              <span className='current-status'>({subscriptionStatus})</span>
            </div>
            <Button className='manage-button' onClick={handleManageSubscription} disabled={actionLoading}>
              {actionLoading ? 'Loading...' : 'Manage Subscription'}
            </Button>
          </div>
        )}
      </div>

      <div className='subscription-content'>
        <div className='billing-toggle'>
          <span className={billingPeriod === 'monthly' ? 'active' : ''}>Monthly</span>
          <button
            className='toggle-switch'
            onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
          >
            <div className={`toggle-slider ${billingPeriod === 'yearly' ? 'active' : ''}`} />
          </button>
          <span className={billingPeriod === 'yearly' ? 'active' : ''}>
            Yearly
            <span className='savings-badge'>Save up to 20%</span>
          </span>
        </div>

        <div className='plans-grid'>
          {plans.map(plan => {
            const price = getPrice(plan, billingPeriod);
            const savings = billingPeriod === 'yearly' ? getYearlySavings(plan) : null;

            return (
              <div
                key={plan.id}
                className={`plan-card ${isCurrentTier(plan.tier) ? 'current' : ''} ${
                  isDowngrade(plan.tier) ? 'downgrade' : ''
                } ${plan.tier.toLowerCase()}`}
              >
                {plan.tier === 'PRO' && <div className='popular-badge'>Most Popular</div>}

                <div className='plan-header'>
                  <div className='plan-icon' style={{ color: getTierColor(plan.tier) }}>
                    {getTierIcon(plan.tier)}
                  </div>
                  <h3 className='plan-name'>{plan.displayName}</h3>
                  {isCurrentTier(plan.tier) && <span className='current-badge'>Current</span>}
                </div>

                <div className='plan-pricing'>
                  <span className='price'>${price}</span>
                  <span className='period'>/{billingPeriod === 'monthly' ? 'month' : 'year'}</span>
                  {savings && savings.amount > 0 && (
                    <div className='savings'>
                      Save ${savings.amount}/year ({savings.percentage}% off)
                    </div>
                  )}
                </div>

                <div className='plan-credits'>
                  <strong>{plan.monthlyCredits.toLocaleString()} credits</strong> per month
                </div>

                <ul className='plan-features'>
                  {getPlanFeatures(plan).map((feature, index) => (
                    <li key={index}>
                      <CheckmarkIcon size={16} />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button
                  className={`plan-button ${
                    isCurrentTier(plan.tier) ? 'current' : isDowngrade(plan.tier) ? 'downgrade' : 'upgrade'
                  }`}
                  onClick={() => handleUpgrade(plan.id, billingPeriod)}
                  disabled={isCurrentTier(plan.tier) || actionLoading}
                >
                  {isCurrentTier(plan.tier) ? 'Current Plan' : isDowngrade(plan.tier) ? 'Downgrade' : 'Upgrade'}
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Subscription;
